import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, defer, forkJoin, map, Observable, of, Subscription, switchMap, tap, throwError, timeout } from 'rxjs';

import { api, ApiResponse, CurrentUser, PermissionMap } from './api';

const TOKEN_KEY = 'c2b_admin_token';
const USER_KEY = 'c2b_admin_user';
const PERMISSIONS_KEY = 'c2b_admin_permissions';
const SESSION_BOOTSTRAP_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private startup?: Subscription;
  private sessionRevision = 0;
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly token = signal<string | null>(this.readJson<string>(TOKEN_KEY));
  readonly user = signal<CurrentUser | null>(this.readJson<CurrentUser>(USER_KEY));
  readonly permissions = signal<PermissionMap>(this.readJson<PermissionMap>(PERMISSIONS_KEY) ?? {});
  readonly permissionsReady = signal(false);
  readonly isAuthenticated = computed(() => this.token() !== null);

  constructor() {
    if (this.token()) {
      const revision = this.sessionRevision;
      // Restore after DI construction so the auth interceptor can resolve this service.
      queueMicrotask(() => {
        if (revision !== this.sessionRevision) return;
        this.startup = this.restoreSession().subscribe({error: () => {
          if (revision === this.sessionRevision) this.clearSession();
        }});
      });
    }
  }

  login(email: string, password: string): Observable<void> {
    return this.http.post<ApiResponse<{ token: string }>>(`${api.external}sign-in`, { email, password }).pipe(
      map((response) => response.data.token),
      tap((token) => {
        this.writeJson(TOKEN_KEY, token);
        this.token.set(token);
      }),
      switchMap(() => this.restoreSession()),
    );
  }

  restoreSession(): Observable<void> {
    if (!this.token()) {
      return of(void 0);
    }

    const revision = this.sessionRevision;
    return forkJoin({
      permissions: this.http
        .get<ApiResponse<{ permissions: PermissionMap }>>(`${api.frontend}entity/permissions`)
        .pipe(map((response) => response.data.permissions)),
      user: this.http
        .get<ApiResponse<{ user: CurrentUser }>>(`${api.external}user/current`)
        .pipe(map((response) => response.data.user)),
    }).pipe(
      // A gateway outage must return control to the sign-in form instead of
      // leaving it indefinitely in the “Signing in…” state.
      timeout(SESSION_BOOTSTRAP_TIMEOUT_MS),
      tap(({ permissions, user }) => {
        if (revision !== this.sessionRevision) throw new Error('Session was replaced.');
        this.permissions.set(permissions);
        this.user.set(user);
        this.permissionsReady.set(true);
        this.writeJson(PERMISSIONS_KEY, permissions);
        this.writeJson(USER_KEY, user);
      }),
      map(() => void 0),
      catchError((error) => {
        if (revision === this.sessionRevision) this.permissionsReady.set(true);
        return throwError(() => error);
      }),
    );
  }

  canManageFields(entity: string): boolean {
    return this.permissions()[entity]?.includes('manage-fields') ?? false;
  }

  canEnterAdmin(): boolean {
    return this.permissions()['admin-panel']?.includes('access') === true
      || this.canManageFields('transporting_units');
  }

  acceptCrmSession(token: string): Observable<void> {
    return defer(() => {
      this.startup?.unsubscribe();
      this.clearSession();
      if (!token || token.length > 16_384 || /\s/.test(token)) {
        return throwError(() => new Error('Invalid CRM session.'));
      }
      // Use memory for validation; do not persist an unverified CRM token.
      this.token.set(token);
      const revision = this.sessionRevision;
      return this.restoreSession().pipe(
        tap(() => {
          if (!this.canEnterAdmin()) throw new Error('Admin access is not enabled.');
          this.writeJson(TOKEN_KEY, token);
        }),
        catchError(error => {
          if (this.token() === token && this.sessionRevision === revision) this.clearSession();
          return throwError(() => error);
        }),
      );
    });
  }

  canManageSystemSettings(): boolean {
    return this.permissions()['system']?.includes('manage-settings') ?? false;
  }

  canManageSidebarMenu(): boolean {
    return this.permissions()['system']?.includes('manage-menu') ?? false;
  }

  canManageUserAccess(): boolean {
    return this.permissions()['system']?.includes('manage-user-access') ?? false;
  }

  canManageUsers(): boolean {
    return this.permissionsReady() && (this.permissions()['system']?.includes('manage-users') ?? false);
  }

  logout(redirect = true, notifyBackend = true): void {
    const finalize = () => {
      this.clearSession();
      if (redirect) {
        void this.router.navigateByUrl('/login');
      }
    };

    if (!notifyBackend || !this.isAuthenticated()) {
      finalize();
      return;
    }

    this.http.post(`${api.external}sign-out`, {}).pipe(
      catchError(() => of(null)),
    ).subscribe({ complete: finalize });
  }

  private clearSession(): void {
    this.startup?.unsubscribe();
    this.sessionRevision++;
    this.token.set(null);
    this.user.set(null);
    this.permissions.set({});
    this.permissionsReady.set(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
  }

  private readJson<T>(key: string): T | null {
    try {
      const value = localStorage.getItem(key);
      return value === null ? null : (JSON.parse(value) as T);
    } catch {
      return null;
    }
  }

  private writeJson(key: string, value: unknown): void {
    localStorage.setItem(key, JSON.stringify(value));
  }
}
